import React from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  className = '',
  disabled,
  ...props
}) => {
  const baseClasses = [
    'inline-flex items-center justify-center font-medium rounded-lg',
    'transition-all duration-200 ease-in-out',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'active:scale-95'
  ];

  const variantClasses = {
    primary: [
      'bg-blue-600 text-white border-blue-600',
      'hover:bg-blue-700 hover:border-blue-700',
      'focus:ring-blue-500',
      'active:bg-blue-800'
    ],
    secondary: [
      'bg-gray-600 text-white border-gray-600',
      'hover:bg-gray-700 hover:border-gray-700',
      'focus:ring-gray-500',
      'active:bg-gray-800'
    ],
    outline: [
      'bg-transparent text-blue-600 border border-blue-600',
      'hover:bg-blue-50 hover:text-blue-700',
      'focus:ring-blue-500',
      'active:bg-blue-100'
    ],
    ghost: [
      'bg-transparent text-gray-700 border-transparent',
      'hover:bg-gray-100 hover:text-gray-900',
      'focus:ring-gray-500',
      'active:bg-gray-200'
    ],
    danger: [
      'bg-red-600 text-white border-red-600',
      'hover:bg-red-700 hover:border-red-700',
      'focus:ring-red-500',
      'active:bg-red-800'
    ],
    success: [
      'bg-green-600 text-white border-green-600',
      'hover:bg-green-700 hover:border-green-700',
      'focus:ring-green-500',
      'active:bg-green-800'
    ],
    warning: [
      'bg-amber-500 text-white border-amber-500',
      'hover:bg-amber-600 hover:border-amber-600',
      'focus:ring-amber-500',
      'active:bg-amber-700'
    ]
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm min-h-[32px]',
    md: 'px-4 py-2 text-sm min-h-[40px]',
    lg: 'px-6 py-3 text-base min-h-[48px]'
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  const classes = [
    ...baseClasses,
    ...variantClasses[variant],
    sizeClasses[size],
    fullWidth ? 'w-full' : '',
    className
  ].filter(Boolean).join(' ');

  const renderIcon = (iconElement: React.ReactNode) => {
    if (!iconElement) return null;
    
    return (
      <span className={`${iconSizeClasses[size]} flex-shrink-0`}>
        {iconElement}
      </span>
    );
  };

  const renderLoadingSpinner = () => (
    <svg 
      className={`animate-spin ${iconSizeClasses[size]} flex-shrink-0`}
      fill="none" 
      viewBox="0 0 24 24"
    >
      <circle 
        className="opacity-25" 
        cx="12" 
        cy="12" 
        r="10" 
        stroke="currentColor" 
        strokeWidth="4"
      />
      <path 
        className="opacity-75" 
        fill="currentColor" 
        d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <button
      className={classes}
      disabled={disabled || loading}
      {...props}
    >
      {loading && renderLoadingSpinner()}
      
      {!loading && icon && iconPosition === 'left' && renderIcon(icon)}
      
      {children && (
        <span className={`
          ${(icon && iconPosition === 'left') || loading ? 'ml-2' : ''}
          ${icon && iconPosition === 'right' ? 'mr-2' : ''}
        `}>
          {children}
        </span>
      )}
      
      {!loading && icon && iconPosition === 'right' && renderIcon(icon)}
    </button>
  );
};

export default Button;